import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/PeriodTypeModal";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface PeriodTypeModalProps {
  selectedObject: any;
  selectedInfo: any;
  handleCloseModal: () => void;
  handleSave: (objectType: any, payload?: any) => void;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  setSelectedInfo: (info: any) => void;
  setTempFormData: (data: any) => void;
  parseInfoValue: (infoValue: string) => any;
  filter: string;
  updateState: (newState: any) => void;
}

const PeriodTypeModal: React.FC<PeriodTypeModalProps> = ({
  selectedObject,
  selectedInfo,
  handleCloseModal,
  handleSave,
  handleInputChange,
  setSelectedInfo,
  setTempFormData,
  parseInfoValue,
  filter,
  updateState,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-periodtypemodal",
  });

  const [localFormData, setLocalFormData] = useState({
    periodTypeName: "",
    periodTypeDescription: "",
    startTime: "08:00",
    endTime: "18:00",
    isActive: true,
    products: [],
    paymentMethods: [],
    discounts: [],
    taxes: [],
    employees: [],
    sequence: 1,
  });

  useEffect(() => {
    if (selectedObject) {
      setLocalFormData((prev) => ({
        ...prev,
        periodTypeName: selectedObject.periodTypeName || "",
        // A coluna em acd."ActPeriodType" é `periodTypeDescription`. Lendo
        // `periodDescription`, a descrição vinha SEMPRE vazia na edição, e
        // salvar por cima apagaria o que estava gravado.
        periodTypeDescription: selectedObject.periodTypeDescription || "",
        ...parseInfoValue(selectedObject.periodTypeInfoValue || "{}"),
      }));
    }
  }, [selectedObject]);

  const handleLocalChange = (field: string, value: any) => {
    setLocalFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!localFormData.periodTypeName) {
      alert("Por favor, preencha o nome do período.");
      return;
    }
    handleSave("periodType", localFormData);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatUser = (userId: string) => {
    const USER_MAP: { [key: string]: string } = {
      "e16904f8-1d9b-4579-907c-2cf5dcc9979f": "Administrador",
      default: "Usuário do Sistema",
    };
    return USER_MAP[userId] || USER_MAP.default;
  };

  const renderInfoContent = () => {
    switch (selectedInfo?.periodTypeInfoType) {
      case 1: // Descontos
        return (
          <AlfredoStyles.InfoSection>
            <AlfredoStyles.SectionTitle>
              Políticas de Desconto
            </AlfredoStyles.SectionTitle>
            <AlfredoStyles.AlertMessage type="info">
              <AlfredoStyles.MaterialIcons>
                local_offer
              </AlfredoStyles.MaterialIcons>
              Configure as políticas de desconto aplicáveis neste período.
            </AlfredoStyles.AlertMessage>
          </AlfredoStyles.InfoSection>
        );

      case 2: // Taxas
        return (
          <AlfredoStyles.InfoSection>
            <AlfredoStyles.SectionTitle>
              Taxas e Encargos
            </AlfredoStyles.SectionTitle>
            <AlfredoStyles.AlertMessage type="info">
              <AlfredoStyles.MaterialIcons>receipt</AlfredoStyles.MaterialIcons>
              Configure as taxas e encargos aplicáveis neste período.
            </AlfredoStyles.AlertMessage>
          </AlfredoStyles.InfoSection>
        );

      case 3: // Produtos
        return (
          <AlfredoStyles.InfoSection>
            <AlfredoStyles.SectionTitle>
              Produtos do Período
            </AlfredoStyles.SectionTitle>
            <AlfredoStyles.AlertMessage type="info">
              <AlfredoStyles.MaterialIcons>
                restaurant
              </AlfredoStyles.MaterialIcons>
              Gerencie os produtos disponíveis durante este período.
            </AlfredoStyles.AlertMessage>
          </AlfredoStyles.InfoSection>
        );

      case 4: // Formas de Pagamento
        return (
          <AlfredoStyles.InfoSection>
            <AlfredoStyles.SectionTitle>
              Formas de Pagamento
            </AlfredoStyles.SectionTitle>

            <AlfredoStyles.FormGrid>
              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input type="checkbox" defaultChecked />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Dinheiro
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input type="checkbox" defaultChecked />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Cartão de Crédito
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input type="checkbox" defaultChecked />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Cartão de Débito
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input type="checkbox" />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>PIX</AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input type="checkbox" />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Vale Refeição
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormGrid>
          </AlfredoStyles.InfoSection>
        );

      case 5: // Sequência
        return (
          <AlfredoStyles.InfoSection>
            <AlfredoStyles.SectionTitle>
              Sequência e Ordem
            </AlfredoStyles.SectionTitle>

            <AlfredoStyles.FormGrid>
              <AlfredoStyles.TextInput>
                <input
                  type="number"
                  value={localFormData.sequence}
                  onChange={(e) =>
                    handleLocalChange("sequence", parseInt(e.target.value))
                  }
                  min="1"
                />
                <label>Ordem de Sequência</label>
                <span className="tooltip">
                  Define a ordem de exibição e prioridade do período
                </span>
              </AlfredoStyles.TextInput>
            </AlfredoStyles.FormGrid>
          </AlfredoStyles.InfoSection>
        );

      case 6: // Observações
        return (
          <AlfredoStyles.InfoSection>
            <AlfredoStyles.SectionTitle>
              Observações e Instruções
            </AlfredoStyles.SectionTitle>

            <AlfredoStyles.TextArea fullWidth>
              <textarea
                placeholder="Digite observações importantes sobre este período..."
                rows={6}
              />
              <label>Observações</label>
            </AlfredoStyles.TextArea>
          </AlfredoStyles.InfoSection>
        );

      default:
        return (
          <AlfredoStyles.InfoSection>
            <AlfredoStyles.SectionTitle>
              Informações Gerais do Período
            </AlfredoStyles.SectionTitle>
            <AlfredoStyles.AlertMessage type="info">
              <AlfredoStyles.MaterialIcons>info</AlfredoStyles.MaterialIcons>
              Selecione uma categoria para configurar as opções do período.
            </AlfredoStyles.AlertMessage>
          </AlfredoStyles.InfoSection>
        );
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} large>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-periodtypemodal">
            <AlfredoStyles.MaterialIcons>schedule</AlfredoStyles.MaterialIcons>
            {/* Testar `selectedObject` por si só é sempre verdadeiro: o
                useModal faz `setSelectedObject(object || {})`, e `{}` é truthy.
                Ao criar, o título saía "Período - undefined". Quem responde
                "isto é uma edição?" é o uuid, não a existência do objeto. */}
            {selectedObject?.periodTypeUuid
              ? `Período - ${selectedObject.periodTypeName}`
              : "Novo Período"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.PeriodTypeContainer>
            {/* Informações Principais */}
            <AlfredoStyles.PeriodTypeTopContainer>
              <AlfredoStyles.FormSection>
                <AlfredoStyles.SectionTitle>
                  Informações do Período
                </AlfredoStyles.SectionTitle>

                <AlfredoStyles.FormGrid>
                  <AlfredoStyles.TextInput>
                    <input
                      type="text"
                      value={localFormData.periodTypeName}
                      onChange={(e) =>
                        handleLocalChange("periodTypeName", e.target.value)
                      }
                      placeholder="Ex: Almoço, Jantar, Happy Hour"
                      required
                    />
                    <label>Nome do Período *</label>
                  </AlfredoStyles.TextInput>

                  <AlfredoStyles.TextInput>
                    <input
                      type="text"
                      value={localFormData.periodTypeDescription}
                      onChange={(e) =>
                        handleLocalChange("periodTypeDescription", e.target.value)
                      }
                      placeholder="Descrição do período"
                    />
                    <label>Descrição</label>
                  </AlfredoStyles.TextInput>
                </AlfredoStyles.FormGrid>

                <AlfredoStyles.FormGrid>
                  <AlfredoStyles.TextInput>
                    <input
                      type="time"
                      value={localFormData.startTime}
                      onChange={(e) =>
                        handleLocalChange("startTime", e.target.value)
                      }
                    />
                    <label>Horário de Início</label>
                  </AlfredoStyles.TextInput>

                  <AlfredoStyles.TextInput>
                    <input
                      type="time"
                      value={localFormData.endTime}
                      onChange={(e) =>
                        handleLocalChange("endTime", e.target.value)
                      }
                    />
                    <label>Horário de Término</label>
                  </AlfredoStyles.TextInput>

                  <AlfredoStyles.TextInput>
                    <input
                      type="number"
                      value={localFormData.sequence}
                      onChange={(e) =>
                        handleLocalChange("sequence", parseInt(e.target.value))
                      }
                      min="1"
                    />
                    <label>Sequência</label>
                  </AlfredoStyles.TextInput>
                </AlfredoStyles.FormGrid>

                <AlfredoStyles.SwitchContainer>
                  <AlfredoStyles.SwitchInput
                    type="checkbox"
                    checked={localFormData.isActive}
                    onChange={(e) =>
                      handleLocalChange("isActive", e.target.checked)
                    }
                  />
                  <AlfredoStyles.SwitchLabel>
                    Período Ativo
                    <span className="tooltip">
                      Ative para que este período esteja disponível para uso
                    </span>
                  </AlfredoStyles.SwitchLabel>
                </AlfredoStyles.SwitchContainer>
              </AlfredoStyles.FormSection>

              {/* Metadados. `selectedObject` é `{}` na criação (o useModal faz
                  `object || {}`) e `{}` é truthy: este bloco abria ao CRIAR e
                  mostrava criado em/atualizado em/alterado por de um objeto que
                  não existe. Quem responde "isto é edição?" é o uuid. */}
              {selectedObject?.periodTypeUuid && (
                <AlfredoStyles.FormSection>
                  <AlfredoStyles.SectionTitle>
                    Informações do Sistema
                  </AlfredoStyles.SectionTitle>
                  <AlfredoStyles.InfoGrid>
                    <AlfredoStyles.InfoItem>
                      <AlfredoStyles.InfoLabel>
                        <AlfredoStyles.MaterialIcons>
                          date_range
                        </AlfredoStyles.MaterialIcons>
                        Criado em:
                      </AlfredoStyles.InfoLabel>
                      <AlfredoStyles.InfoValue>
                        {formatDate(selectedObject.createdAt)}
                      </AlfredoStyles.InfoValue>
                    </AlfredoStyles.InfoItem>

                    <AlfredoStyles.InfoItem>
                      <AlfredoStyles.InfoLabel>
                        <AlfredoStyles.MaterialIcons>
                          update
                        </AlfredoStyles.MaterialIcons>
                        Última atualização:
                      </AlfredoStyles.InfoLabel>
                      <AlfredoStyles.InfoValue>
                        {formatDate(selectedObject.updatedAt)}
                      </AlfredoStyles.InfoValue>
                    </AlfredoStyles.InfoItem>

                    <AlfredoStyles.InfoItem>
                      <AlfredoStyles.InfoLabel>
                        <AlfredoStyles.MaterialIcons>
                          person
                        </AlfredoStyles.MaterialIcons>
                        Alterado por:
                      </AlfredoStyles.InfoLabel>
                      <AlfredoStyles.InfoValue>
                        {formatUser(selectedObject.updatedBy)}
                      </AlfredoStyles.InfoValue>
                    </AlfredoStyles.InfoItem>
                  </AlfredoStyles.InfoGrid>
                </AlfredoStyles.FormSection>
              )}
            </AlfredoStyles.PeriodTypeTopContainer>

            {/* Configurações Detalhadas */}
            <AlfredoStyles.PeriodTypeBottom>
              <AlfredoStyles.PeriodTypeInfoContainer>
                <AlfredoStyles.PeriodTypeInfoList>
                  <AlfredoStyles.PeriodTypeInfo
                    selected={!selectedInfo?.periodTypeInfoType}
                    onClick={() => setSelectedInfo({ periodTypeInfoType: 0 })}
                  >
                    <AlfredoStyles.StyledIcon>info</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Visão Geral</div>
                      <small>Informações básicas</small>
                    </div>
                  </AlfredoStyles.PeriodTypeInfo>

                  <AlfredoStyles.PeriodTypeInfo
                    selected={selectedInfo?.periodTypeInfoType === 1}
                    onClick={() => setSelectedInfo({ periodTypeInfoType: 1 })}
                  >
                    <AlfredoStyles.StyledIcon>
                      local_offer
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Descontos</div>
                      <small>Políticas de desconto</small>
                    </div>
                  </AlfredoStyles.PeriodTypeInfo>

                  <AlfredoStyles.PeriodTypeInfo
                    selected={selectedInfo?.periodTypeInfoType === 2}
                    onClick={() => setSelectedInfo({ periodTypeInfoType: 2 })}
                  >
                    <AlfredoStyles.StyledIcon>receipt</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Taxas</div>
                      <small>Taxas e encargos</small>
                    </div>
                  </AlfredoStyles.PeriodTypeInfo>

                  <AlfredoStyles.PeriodTypeInfo
                    selected={selectedInfo?.periodTypeInfoType === 3}
                    onClick={() => setSelectedInfo({ periodTypeInfoType: 3 })}
                  >
                    <AlfredoStyles.StyledIcon>
                      restaurant
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Produtos</div>
                      <small>Cardápio do período</small>
                    </div>
                  </AlfredoStyles.PeriodTypeInfo>

                  <AlfredoStyles.PeriodTypeInfo
                    selected={selectedInfo?.periodTypeInfoType === 4}
                    onClick={() => setSelectedInfo({ periodTypeInfoType: 4 })}
                  >
                    <AlfredoStyles.StyledIcon>
                      payments
                    </AlfredoStyles.StyledIcon>
                    <div>
                      <div>Pagamentos</div>
                      <small>Formas de pagamento</small>
                    </div>
                  </AlfredoStyles.PeriodTypeInfo>

                  <AlfredoStyles.PeriodTypeInfo
                    selected={selectedInfo?.periodTypeInfoType === 5}
                    onClick={() => setSelectedInfo({ periodTypeInfoType: 5 })}
                  >
                    <AlfredoStyles.StyledIcon>sort</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Sequência</div>
                      <small>Ordem e prioridade</small>
                    </div>
                  </AlfredoStyles.PeriodTypeInfo>

                  <AlfredoStyles.PeriodTypeInfo
                    selected={selectedInfo?.periodTypeInfoType === 6}
                    onClick={() => setSelectedInfo({ periodTypeInfoType: 6 })}
                  >
                    <AlfredoStyles.StyledIcon>notes</AlfredoStyles.StyledIcon>
                    <div>
                      <div>Observações</div>
                      <small>Instruções internas</small>
                    </div>
                  </AlfredoStyles.PeriodTypeInfo>
                </AlfredoStyles.PeriodTypeInfoList>

                <AlfredoStyles.PeriodTypeInfoContent>
                  {renderInfoContent()}
                </AlfredoStyles.PeriodTypeInfoContent>
              </AlfredoStyles.PeriodTypeInfoContainer>
            </AlfredoStyles.PeriodTypeBottom>
          </AlfredoStyles.PeriodTypeContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton onClick={handleSubmit}>
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {selectedObject?.periodTypeUuid
                ? "Atualizar Período"
                : "Criar Período"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default PeriodTypeModal;
